import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Clock, ChevronLeft, ChevronRight, Download, CalendarDays,
  CalendarRange, CheckCircle2, XCircle, Timer, AlertTriangle,
  ChevronDown, Tag, Pencil,
} from 'lucide-react';
import { jobCategoryLabel, jobCategoryOptions } from '@/lib/jobCategory';
import { formatHoursDecimal as fmtH } from '@/lib/formatHours';
import UnassignedActivitiesTab from './UnassignedActivitiesTab';

import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_LABELS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAY_LABELS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_FR = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
const MONTH_EN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getMondayStr(offsetWeeks = 0) {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1 + offsetWeeks * 7);
  return d.toISOString().slice(0, 10);
}

function getCurrentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function shiftWeek(mondayStr, delta) {
  const d = new Date(mondayStr + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return d.toISOString().slice(0, 7);
}

export default function TimeTrackingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;
  const dayLabels = language === 'fr' ? DAY_LABELS_FR : DAY_LABELS_EN;
  const monthNames = language === 'fr' ? MONTH_FR : MONTH_EN;

  const [weekStart, setWeekStart] = useState(getMondayStr());
  const [month, setMonth] = useState(getCurrentMonth());
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});
  // Hours editor (admin only) — local-only dialog state
  const [editingActivity, setEditingActivity] = useState(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [savingHours, setSavingHours] = useState(false);

  const userRole = (user?.role || '').toLowerCase();
  const canEditCategory = userRole === 'admin' || userRole === 'admin_enterprise';
  const canEditHours = canEditCategory;
  const catOptions = jobCategoryOptions(language);

  // ISO datetime <-> <input type="datetime-local"> conversions (local time, minutes precision)
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };
  const localInputToIso = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
  };

  const openHoursEditor = (activity) => {
    setEditingActivity(activity);
    setEditClockIn(isoToLocalInput(activity.clock_in));
    setEditClockOut(isoToLocalInput(activity.clock_out));
  };

  const closeHoursEditor = () => {
    setEditingActivity(null);
    setEditClockIn('');
    setEditClockOut('');
  };

  const saveHours = async () => {
    if (!editingActivity) return;
    const inIso = localInputToIso(editClockIn);
    const outIso = editClockOut ? localInputToIso(editClockOut) : '';
    if (!inIso) {
      toast.error(tf('Heure d\'arrivée requise', 'Clock in required'));
      return;
    }
    if (outIso && new Date(outIso) <= new Date(inIso)) {
      toast.error(tf('L\'heure de départ doit être après l\'arrivée', 'Clock out must be after clock in'));
      return;
    }
    setSavingHours(true);
    try {
      await api.patch(`/timetracking/activity/${editingActivity.activity_id}/hours`, {
        clock_in: inIso,
        clock_out: outIso || null,
      });
      toast.success(tf('Heures mises à jour', 'Hours updated'));
      closeHoursEditor();
      await loadWeekly();
    } catch (err) {
      toast.error(err.response?.data?.detail || tf('Erreur de mise à jour', 'Update error'));
    } finally {
      setSavingHours(false);
    }
  };

  const toggleExpand = (eid) => {
    setExpandedRows((prev) => ({ ...prev, [eid]: !prev[eid] }));
  };

  const updateActivityCategory = async (activityId, newCategory) => {
    try {
      await api.patch(`/timetracking/activity/${activityId}/job-category`, {
        job_category: newCategory,
      });
      // Optimistic update on local state to avoid full reload flicker
      setWeeklyData((prev) => {
        if (!prev) return prev;
        const rows = prev.rows.map((r) => ({
          ...r,
          activities: (r.activities || []).map((a) =>
            a.activity_id === activityId ? { ...a, job_category: newCategory } : a
          ),
        }));
        return { ...prev, rows };
      });
      toast.success(tf('Catégorie mise à jour', 'Category updated'));
    } catch {
      toast.error(tf('Erreur de mise à jour', 'Update error'));
    }
  };

  const loadWeekly = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/timetracking/weekly?week_start=${weekStart}`);
      setWeeklyData(data);
    } catch { toast.error(tf('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  }, [weekStart]);

  const loadMonthly = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/timetracking/monthly?month=${month}`);
      setMonthlyData(data);
    } catch { toast.error(tf('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadWeekly(); }, [loadWeekly]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const toggleApproval = async (employeeId, currentApproved) => {
    try {
      await api.put('/timetracking/approve', {
        employee_id: employeeId, week_start: weekStart, approved: !currentApproved,
      });
      loadWeekly();
      toast.success(!currentApproved ? tf('Approuve', 'Approved') : tf('Desapprouve', 'Unapproved'));
    } catch { toast.error(tf('Erreur', 'Error')); }
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_URL}/api/timetracking/export?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resp.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || `timesheet_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(tf('Fichier telecharge', 'File downloaded'));
    } catch { toast.error(tf('Erreur export', 'Export error')); }
  };

  const allEmployees = weeklyData?.rows?.map(r => ({ id: r.employee_id, name: r.employee_name })) || [];
  const filterRows = (rows) => {
    if (!rows) return [];
    if (employeeFilter === 'all') return rows;
    return rows.filter(r => r.employee_id === employeeFilter);
  };

  const weekLabel = weeklyData ? (() => {
    const dates = weeklyData.dates;
    const from = new Date(dates[0] + 'T00:00:00');
    const to = new Date(dates[6] + 'T00:00:00');
    const fmt = (d) => d.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short' });
    return `${fmt(from)} - ${fmt(to)}, ${from.getFullYear()}`;
  })() : '';

  const [yr, mn] = month.split('-').map(Number);
  const monthLabel = `${monthNames[mn]} ${yr}`;

  return (
    <div className="space-y-6" data-testid="timetracking-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg"><Clock className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">{tf('Feuille de temps', 'Timesheet')}</h1>
            <p className="text-muted-foreground">{tf('Suivi des heures a partir des pointages mobiles', 'Track hours from mobile punches')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {allEmployees.length > 0 && (
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="employee-filter">
                <SelectValue placeholder={tf('Tous les employes', 'All employees')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tf('Tous les employes', 'All employees')}</SelectItem>
                {allEmployees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn">
            <Download className="h-4 w-4 mr-2" />{tf('Export Excel', 'Export Excel')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="weekly" className="space-y-4">
        <TabsList>
          <TabsTrigger value="weekly" data-testid="tab-weekly"><CalendarDays className="h-4 w-4 mr-2" />{tf('Hebdomadaire', 'Weekly')}</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly"><CalendarRange className="h-4 w-4 mr-2" />{tf('Mensuel', 'Monthly')}</TabsTrigger>
          <TabsTrigger value="unassigned" data-testid="tab-unassigned"><AlertTriangle className="h-4 w-4 mr-2" />{tf('Pointages non assignés', 'Unassigned punches')}</TabsTrigger>
        </TabsList>

        {/* ── WEEKLY ── */}
        <TabsContent value="weekly">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" onClick={() => setWeekStart(s => shiftWeek(s, -1))} data-testid="prev-week"><ChevronLeft className="h-4 w-4" /></Button>
                  <CardTitle className="text-base">{weekLabel}</CardTitle>
                  <Button variant="outline" size="icon" onClick={() => setWeekStart(s => shiftWeek(s, 1))} data-testid="next-week"><ChevronRight className="h-4 w-4" /></Button>
                </div>
                {weeklyData?.is_past && <Badge variant="secondary">{tf('Archivee', 'Archived')}</Badge>}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
              ) : !weeklyData?.rows?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Timer className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>{tf('Aucun pointage cette semaine', 'No punches this week')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-3 font-semibold min-w-[160px]">{tf('Employe', 'Employee')}</th>
                        {weeklyData.dates.map((d, i) => (
                          <th key={d} className="text-center p-3 font-semibold min-w-[70px]">
                            <div>{dayLabels[i]}</div>
                            <div className="text-xs text-muted-foreground font-normal">{d.slice(5)}</div>
                          </th>
                        ))}
                        <th className="text-center p-3 font-semibold min-w-[70px]">{tf('Total', 'Total')}</th>
                        <th className="text-center p-3 font-semibold min-w-[70px]">{tf('Suppl.', 'OT')}</th>
                        <th className="text-center p-3 font-semibold min-w-[100px]">{tf('Statut', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filterRows(weeklyData.rows).map(row => {
                        const isExpanded = !!expandedRows[row.employee_id];
                        const acts = row.activities || [];
                        const colSpan = weeklyData.dates.length + 4;
                        return (
                        <Fragment key={row.employee_id}>
                        <tr className="hover:bg-muted/20" data-testid={`weekly-row-${row.employee_id}`}>
                          <td className="p-3 font-medium">
                            <div className="flex items-center gap-2">
                              {acts.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(row.employee_id)}
                                  className="p-0.5 rounded hover:bg-muted"
                                  data-testid={`expand-${row.employee_id}`}
                                  title={tf('Voir les pointages', 'View punches')}
                                >
                                  <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                                </button>
                              )}
                              <span>{row.employee_name}</span>
                            </div>
                          </td>
                          {weeklyData.dates.map(d => {
                            const h = row.days[d] || 0;
                            return (
                              <td key={d} className={`text-center p-3 tabular-nums ${h > 0 ? 'font-medium' : 'text-muted-foreground'}`}>
                                {h > 0 ? fmtH(h) : '-'}
                              </td>
                            );
                          })}
                          <td className="text-center p-3 font-bold tabular-nums">{fmtH(row.total)}</td>
                          <td className="text-center p-3 tabular-nums">
                            {row.overtime > 0 ? (
                              <Badge variant="destructive" className="text-xs"><AlertTriangle className="h-3 w-3 mr-1" />{fmtH(row.overtime)}</Badge>
                            ) : <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="text-center p-3">
                            <Button
                              variant={row.approved ? 'default' : 'outline'}
                              size="sm"
                              className={row.approved ? 'bg-green-600 hover:bg-green-700' : ''}
                              onClick={() => toggleApproval(row.employee_id, row.approved)}
                              data-testid={`approve-${row.employee_id}`}
                            >
                              {row.approved
                                ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{tf('Approuve', 'Approved')}</>
                                : <><XCircle className="h-3.5 w-3.5 mr-1" />{tf('Non approuve', 'Not approved')}</>}
                            </Button>
                          </td>
                        </tr>
                        {isExpanded && acts.length > 0 && (
                          <tr className="bg-muted/10">
                            <td colSpan={colSpan} className="p-0">
                              <div className="px-6 py-3 space-y-2" data-testid={`activities-${row.employee_id}`}>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                                  {tf('Pointages de la semaine', 'Punches this week')}
                                </div>
                                {acts.map((a) => (
                                  <div
                                    key={a.activity_id}
                                    className="flex flex-wrap items-center gap-3 text-sm py-1.5 border-b border-dashed last:border-0"
                                    data-testid={`activity-${a.activity_id}`}
                                  >
                                    <span className="font-mono text-xs text-muted-foreground min-w-[90px]">{a.date}</span>
                                    <span className="tabular-nums min-w-[60px]">{a.hours > 0 ? fmtH(a.hours) : tf('En cours', 'In progress')}</span>
                                    {canEditHours && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => openHoursEditor(a)}
                                        data-testid={`edit-hours-${a.activity_id}`}
                                        title={tf('Modifier les heures', 'Edit hours')}
                                      >
                                        <Pencil className="h-3 w-3 mr-1" />
                                        {tf('Heures', 'Hours')}
                                      </Button>
                                    )}
                                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                                    {canEditCategory ? (
                                      <Select
                                        value={a.job_category || 'unset'}
                                        onValueChange={(val) => updateActivityCategory(a.activity_id, val === 'unset' ? null : val)}
                                      >
                                        <SelectTrigger
                                          className="h-7 text-xs w-[240px]"
                                          data-testid={`category-select-${a.activity_id}`}
                                        >
                                          <SelectValue placeholder={tf('Non spécifié', 'Not set')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="unset">{tf('Non spécifié', 'Not set')}</SelectItem>
                                          {catOptions.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <Badge variant="secondary" className="text-xs" data-testid={`category-label-${a.activity_id}`}>
                                        {jobCategoryLabel(a.job_category, language)}
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                    {filterRows(weeklyData.rows).length > 1 && (
                      <tfoot>
                        <tr className="bg-muted/50 font-bold">
                          <td className="p-3">{tf('TOTAL', 'TOTAL')}</td>
                          {weeklyData.dates.map(d => {
                            const sum = filterRows(weeklyData.rows).reduce((acc, r) => acc + (r.days[d] || 0), 0);
                            return <td key={d} className="text-center p-3 tabular-nums">{sum > 0 ? fmtH(sum) : '-'}</td>;
                          })}
                          <td className="text-center p-3 tabular-nums">{fmtH(filterRows(weeklyData.rows).reduce((a, r) => a + r.total, 0))}</td>
                          <td className="text-center p-3 tabular-nums">{fmtH(filterRows(weeklyData.rows).reduce((a, r) => a + r.overtime, 0))}</td>
                          <td />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── MONTHLY ── */}
        <TabsContent value="monthly">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => setMonth(m => shiftMonth(m, -1))} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
                <CardTitle className="text-base">{monthLabel}</CardTitle>
                <Button variant="outline" size="icon" onClick={() => setMonth(m => shiftMonth(m, 1))} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
              ) : !monthlyData?.rows?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Timer className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>{tf('Aucun pointage ce mois', 'No punches this month')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-3 font-semibold min-w-[160px]">{tf('Employe', 'Employee')}</th>
                        {monthlyData.weeks.map(w => (
                          <th key={w.week_start} className="text-center p-3 font-semibold min-w-[100px]">
                            <div className="text-xs">{w.label}</div>
                          </th>
                        ))}
                        <th className="text-center p-3 font-semibold min-w-[80px]">{tf('Total mois', 'Month total')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filterRows(monthlyData.rows).map(row => (
                        <tr key={row.employee_id} className="hover:bg-muted/20" data-testid={`monthly-row-${row.employee_id}`}>
                          <td className="p-3 font-medium">{row.employee_name}</td>
                          {monthlyData.weeks.map(w => {
                            const h = row.weeks[w.week_start] || 0;
                            return (
                              <td key={w.week_start} className={`text-center p-3 tabular-nums ${h > 0 ? 'font-medium' : 'text-muted-foreground'}`}>
                                {h > 0 ? fmtH(h) : '-'}
                              </td>
                            );
                          })}
                          <td className="text-center p-3 font-bold tabular-nums">{fmtH(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {filterRows(monthlyData.rows).length > 1 && (
                      <tfoot>
                        <tr className="bg-muted/50 font-bold">
                          <td className="p-3">{tf('TOTAL', 'TOTAL')}</td>
                          {monthlyData.weeks.map(w => {
                            const sum = filterRows(monthlyData.rows).reduce((a, r) => a + (r.weeks[w.week_start] || 0), 0);
                            return <td key={w.week_start} className="text-center p-3 tabular-nums">{sum > 0 ? fmtH(sum) : '-'}</td>;
                          })}
                          <td className="text-center p-3 tabular-nums">{fmtH(filterRows(monthlyData.rows).reduce((a, r) => a + r.total, 0))}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* P-FLOAT — Pointages non assignés (floating punches) */}
        <TabsContent value="unassigned">
          <UnassignedActivitiesTab tf={tf} language={language} />
        </TabsContent>
      </Tabs>

      {/* Hours editor — admin only */}
      <Dialog open={!!editingActivity} onOpenChange={(o) => { if (!o) closeHoursEditor(); }}>
        <DialogContent data-testid="edit-hours-dialog">
          <DialogHeader>
            <DialogTitle>{tf('Modifier les heures', 'Edit hours')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-clock-in">{tf('Arrivée (clock in)', 'Clock in')}</Label>
              <Input
                id="edit-clock-in"
                type="datetime-local"
                value={editClockIn}
                onChange={(e) => setEditClockIn(e.target.value)}
                data-testid="edit-clock-in-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-clock-out">{tf('Départ (clock out)', 'Clock out')}</Label>
              <Input
                id="edit-clock-out"
                type="datetime-local"
                value={editClockOut}
                onChange={(e) => setEditClockOut(e.target.value)}
                data-testid="edit-clock-out-input"
              />
              <p className="text-xs text-muted-foreground">
                {tf('Laisser vide si le pointage est encore en cours.', 'Leave empty if the punch is still in progress.')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeHoursEditor} disabled={savingHours} data-testid="cancel-hours-btn">
              {tf('Annuler', 'Cancel')}
            </Button>
            <Button onClick={saveHours} disabled={savingHours} data-testid="save-hours-btn">
              {savingHours ? tf('Sauvegarde...', 'Saving...') : tf('Enregistrer', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
